// API 클라이언트 — Bearer 토큰(우선) + CSRF 헤더(쿠키 흐름 보조) + Accept-Language 자동 첨부
// VITE_API_BASE 로 백엔드 절대 URL 주입. 비어 있으면 상대 경로(/api) 사용 (Vite dev proxy).
import { currentLang } from './i18n'

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/+$/, '') ?? ''

const SAFE = new Set(['GET', 'HEAD', 'OPTIONS'])
const TOKEN_KEY = 'mes_access_token'
const TOKEN_EXP_KEY = 'mes_access_exp'

function readCookie(name: string): string | null {
  const v = document.cookie
    .split('; ')
    .find((r) => r.startsWith(name + '='))
  return v ? decodeURIComponent(v.split('=').slice(1).join('=')) : null
}

export interface ApiError {
  code: string
  message: string
  detail?: unknown
  status: number
}

export class ApiClient {
  base: string
  private token: string | null = null
  private exp: number | null = null
  private listeners = new Set<(token: string | null) => void>()

  constructor(base = '') {
    this.base = base
    // sessionStorage 복원 (탭 단위)
    try {
      const t = sessionStorage.getItem(TOKEN_KEY)
      const e = sessionStorage.getItem(TOKEN_EXP_KEY)
      if (t && e && Number(e) > Date.now() / 1000) {
        this.token = t
        this.exp = Number(e)
      } else if (t) {
        sessionStorage.removeItem(TOKEN_KEY)
        sessionStorage.removeItem(TOKEN_EXP_KEY)
      }
    } catch { /* ignore (private mode, etc.) */ }
  }

  setToken(token: string | null, expiresInSec?: number) {
    this.token = token
    this.exp = token && expiresInSec ? Math.floor(Date.now() / 1000) + expiresInSec - 30 : null
    try {
      if (token) {
        sessionStorage.setItem(TOKEN_KEY, token)
        if (this.exp) sessionStorage.setItem(TOKEN_EXP_KEY, String(this.exp))
      } else {
        sessionStorage.removeItem(TOKEN_KEY)
        sessionStorage.removeItem(TOKEN_EXP_KEY)
      }
    } catch { /* ignore */ }
    for (const l of this.listeners) l(token)
  }

  onTokenChange(cb: (token: string | null) => void) {
    this.listeners.add(cb)
    return () => this.listeners.delete(cb)
  }

  getToken(): string | null {
    if (this.token && this.exp && this.exp < Date.now() / 1000) {
      this.setToken(null)
      return null
    }
    return this.token
  }

  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const method = (init.method ?? 'GET').toUpperCase()
    const headers = new Headers(init.headers)
    if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')

    const token = this.getToken()
    if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`)

    if (!SAFE.has(method)) {
      const csrf = readCookie('csrf_token')
      if (csrf) headers.set('X-CSRF-Token', csrf)
    }
    if (!headers.has('Accept-Language')) headers.set('Accept-Language', currentLang())

    const res = await fetch(this.base + path, {
      ...init,
      method,
      // 크로스 사이트라도 쿠키 흐름 보조 위해 include — 백엔드 CORS가 화이트리스트로 검증
      credentials: 'include',
      headers,
      mode: this.base.startsWith('http') ? 'cors' : 'same-origin',
    })

    let body: any = null
    try { body = await res.json() } catch { /* noop */ }

    if (!res.ok) {
      // 401이면 토큰 폐기 (만료/세션 revoke 등)
      if (res.status === 401) this.setToken(null)
      const err: ApiError = {
        code: body?.error?.code ?? 'UNKNOWN',
        message: body?.error?.message ?? '요청에 실패했습니다.',
        detail: body?.error?.detail,
        status: res.status,
      }
      throw err
    }
    return (body?.data ?? body) as T
  }

  get<T>(path: string): Promise<T> { return this.request<T>(path) }
  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined })
  }
  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined })
  }
}

export const api = new ApiClient(`${API_BASE}/api`)
